import React from 'react';
import { CalendarDays, MapPin, Clock } from 'lucide-react';
import { formatEventTime, formatDateLong } from '../../lib/time';
import { getFontTheme, ensureFontLoaded } from '../../lib/fontThemes';

export const FLYER_LAYOUTS = [
  { id: 'classic', name: 'Classic', desc: 'Gradient header, two-column details' },
  { id: 'poster', name: 'Bold Poster', desc: 'Big centered title on color' },
  { id: 'photo', name: 'Photo-forward', desc: 'Large cover image hero' },
  { id: 'minimal', name: 'Minimal', desc: 'Clean, lots of whitespace' },
  { id: 'festive', name: 'Festive', desc: 'Playful tinted detail cards' },
  { id: 'editorial', name: 'Editorial', desc: 'Magazine-style accent bar' },
];

// ---- Shared sub-blocks ----
const DetailItem = ({ icon: Icon, label, value, sub, link, color, plain }) => (
  <div className="flex items-start gap-3">
    {!plain && (
      <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
    )}
    <div>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-medium text-foreground mt-0.5">{value}</div>
      {sub && <div className="text-sm text-muted-foreground mt-0.5">{sub}</div>}
      {link && <a href={link} target="_blank" rel="noreferrer" className="text-sm font-medium mt-1 inline-block no-print" style={{ color }}>Open in Google Maps →</a>}
    </div>
  </div>
);

const Shell = ({ children, style, logo, ...rest }) => (
  <div className="print-area tg-fontscope relative mx-auto w-full max-w-[800px] bg-white border border-border rounded-2xl shadow-[0_20px_60px_rgba(17,24,39,0.12)] overflow-hidden" data-testid="flyer-canvas" style={style} {...rest}>
    {logo ? (
      <img src={logo} alt="" className="absolute top-4 left-4 z-10 h-10 max-w-[140px] object-contain drop-shadow-[0_1px_3px_rgba(0,0,0,0.35)]" />
    ) : null}
    {children}
  </div>
);

export const FlyerCanvas = ({ event }) => {
  if (!event) return null;
  const theme = event.theme_color || '#2F6B57';
  const accent = event.accent_color || '#C86B4A';
  const layout = (event.flyer_layout || 'classic').toLowerCase();
  const ft = getFontTheme(event.font_theme);
  const fontVars = { '--tg-font-display': ft.display, '--tg-font-body': ft.body };
  ensureFontLoaded(event.font_theme);
  const logo = event.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${event.logo_url}` : '';
  const when = event.date ? formatDateLong(event.date) : 'TBA';
  const timeStr = (event.time || event.end_time) ? formatEventTime(event) : '';
  const mapsUrl = event.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`
    : null;

  const whenItem = (plain) => (
    <DetailItem icon={CalendarDays} label="When" value={when} color={theme} plain={plain}
      sub={timeStr ? timeStr : null} />
  );
  const whereItem = (plain) => (
    <DetailItem icon={MapPin} label="Where" value={event.location_name || 'TBA'} color={accent} plain={plain}
      sub={event.address || null} link={mapsUrl} />
  );

  const descBlock = event.description && (
    <div className="mt-7 pt-6 border-t border-border print-break-avoid">
      <p className="text-foreground/80 leading-relaxed whitespace-pre-line">{event.description}</p>
    </div>
  );

  const footer = (
    <div className="px-8 sm:px-12 py-4 text-center text-xs text-muted-foreground border-t border-border" style={{ background: theme + '08' }}>
      Thrive Gatherings · thrivegathering.life
    </div>
  );

  // ---- POSTER ----
  if (layout === 'poster') {
    return (
      <Shell style={fontVars} logo={logo}>
        <div className="px-8 sm:px-12 py-14 text-center text-white print-break-avoid" style={{ background: `linear-gradient(135deg, ${theme}, ${accent})` }}>
          <div className="text-xs uppercase tracking-[0.3em] text-white/80">Thrive Gatherings · You&apos;re Invited</div>
          <h1 className="font-display text-5xl sm:text-6xl font-semibold mt-4 leading-[1.05]">{event.title}</h1>
          {event.host && <div className="text-white/85 mt-3">Hosted by {event.host}</div>}
          <div className="mt-6 text-lg font-medium tabular-nums">{when}{timeStr && ` · ${timeStr}`}</div>
          <div className="text-white/90">{event.location_name || 'TBA'}</div>
        </div>
        <div className="px-8 sm:px-12 py-8">
          {event.description && <p className="text-foreground/80 leading-relaxed whitespace-pre-line text-center">{event.description}</p>}
        </div>
        {footer}
      </Shell>
    );
  }

  // ---- PHOTO-FORWARD ----
  if (layout === 'photo') {
    return (
      <Shell style={fontVars} logo={logo}>
        <div className="relative print-break-avoid" style={{ aspectRatio: '16 / 7', background: `linear-gradient(135deg, ${theme}, ${accent})` }}>
          {event.cover_image && <img src={event.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.05))' }} />
          <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12">
            <div className="text-xs uppercase tracking-[0.2em] text-white/85">You&apos;re Invited</div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold text-white mt-2 leading-tight">{event.title}</h1>
            {event.host && <div className="text-white/85 mt-2">Hosted by {event.host}</div>}
          </div>
        </div>
        <div className="px-8 sm:px-12 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 print-break-avoid">{whenItem()}{whereItem()}</div>
          {descBlock}
        </div>
        {footer}
      </Shell>
    );
  }

  // ---- MINIMAL ----
  if (layout === 'minimal') {
    return (
      <Shell style={{ borderTop: `5px solid ${accent}`, ...fontVars }} logo={logo}>
        <div className="px-8 sm:px-14 pt-12 pb-2 print-break-avoid">
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Thrive Gatherings · You&apos;re Invited</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3 leading-tight" style={{ color: theme }}>{event.title}</h1>
          {event.host && <div className="text-muted-foreground mt-2">Hosted by {event.host}</div>}
        </div>
        <div className="px-8 sm:px-14 py-8">
          <div className="space-y-3 print-break-avoid">
            <div className="flex gap-3 text-foreground"><span className="text-[11px] uppercase tracking-widest text-muted-foreground w-16 pt-1">When</span><span className="font-medium">{when}{timeStr && <span className="text-muted-foreground font-normal"> · {timeStr}</span>}</span></div>
            <div className="flex gap-3 text-foreground"><span className="text-[11px] uppercase tracking-widest text-muted-foreground w-16 pt-1">Where</span><span className="font-medium">{event.location_name || 'TBA'}{event.address && <span className="block text-sm text-muted-foreground font-normal">{event.address}</span>}</span></div>
          </div>
          {descBlock}
        </div>
        {footer}
      </Shell>
    );
  }

  // ---- FESTIVE ----
  if (layout === 'festive') {
    return (
      <Shell style={fontVars} logo={logo}>
        <div className="relative px-8 sm:px-12 pt-12 pb-10 text-center text-white print-break-avoid overflow-hidden" style={{ background: `linear-gradient(135deg, ${theme}, ${accent})` }}>
          <div className="absolute -top-6 -left-6 h-24 w-24 rounded-full bg-white/10" />
          <div className="absolute top-8 right-8 h-12 w-12 rounded-full bg-white/10" />
          <div className="absolute -bottom-8 right-1/3 h-20 w-20 rounded-full bg-white/10" />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.25em] text-white/85">Let&apos;s Celebrate!</div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3 leading-tight">{event.title}</h1>
            {event.host && <div className="text-white/85 mt-2">Hosted by {event.host}</div>}
          </div>
        </div>
        <div className="px-8 sm:px-12 py-8">
          <div className="grid grid-cols-2 gap-4 print-break-avoid">
            <div className="rounded-2xl p-5 text-center" style={{ background: theme + '12' }}>
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: theme }}>When</div>
              <div className="font-medium text-foreground mt-1">{when}</div>
              {timeStr && <div className="text-sm text-muted-foreground tabular-nums">{timeStr}</div>}
            </div>
            <div className="rounded-2xl p-5 text-center" style={{ background: accent + '12' }}>
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: accent }}>Where</div>
              <div className="font-medium text-foreground mt-1">{event.location_name || 'TBA'}</div>
              {event.address && <div className="text-sm text-muted-foreground">{event.address}</div>}
            </div>
          </div>
          {descBlock}
        </div>
        {footer}
      </Shell>
    );
  }

  // ---- EDITORIAL ----
  if (layout === 'editorial') {
    return (
      <Shell style={fontVars} logo={logo}>
        <div className="flex print-break-avoid">
          <div className="w-2.5 flex-shrink-0" style={{ background: accent }} />
          <div className="flex-1 px-8 sm:px-12 pt-10 pb-6">
            <div className="text-[11px] uppercase tracking-[0.3em] font-semibold" style={{ color: theme }}>The Invitation</div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3 leading-tight text-foreground">{event.title}</h1>
            {event.host && <div className="text-muted-foreground mt-2">Hosted by {event.host}</div>}
          </div>
        </div>
        <div className="px-8 sm:px-12 pb-8">
          <div className="grid grid-cols-2 gap-6 pt-5 border-t-2 border-foreground print-break-avoid">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">When</div>
              <div className="font-medium text-foreground mt-1">{when}</div>
              {timeStr && <div className="text-sm text-muted-foreground tabular-nums">{timeStr}</div>}
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Where</div>
              <div className="font-medium text-foreground mt-1">{event.location_name || 'TBA'}</div>
              {event.address && <div className="text-sm text-muted-foreground">{event.address}</div>}
            </div>
          </div>
          {descBlock}
        </div>
        {footer}
      </Shell>
    );
  }

  // ---- CLASSIC (default) ----
  return (
    <Shell style={fontVars} logo={logo}>
      <div className="relative px-8 sm:px-12 pt-10 pb-8 print-break-avoid" style={{ background: `linear-gradient(135deg, ${theme}, ${accent})` }}>
        {event.cover_image && <img src={event.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="relative">
          <div className="text-xs uppercase tracking-[0.2em] text-white/85">You&apos;re Invited</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold text-white mt-2 leading-tight">{event.title}</h1>
          {event.host && <div className="text-white/85 mt-2">Hosted by {event.host}</div>}
        </div>
      </div>
      <div className="px-8 sm:px-12 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 print-break-avoid">{whenItem()}{whereItem()}</div>
        {descBlock}
      </div>
      {footer}
    </Shell>
  );
};
